import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { toast } from 'react-hot-toast';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    XMarkIcon,
    PhotoIcon,
    ArrowUpIcon,
    ArrowDownIcon,
} from '@heroicons/react/24/outline';

interface Partner {
    _id: string;
    title: string;
    logo: string;
    link: string;
    isActive: boolean;
    displayOrder: number;
    createdAt: string;
}

const PartnersManagement: React.FC = () => {
    const [partners, setPartners] = useState<Partner[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        logo: '',
        link: '',
        isActive: true,
        displayOrder: 0,
    });

    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        try {
            setLoading(true);
            const response = await api.get('/partners/admin');
            setPartners(response.data.data || []);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch partners');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (partner?: Partner) => {
        if (partner) {
            setSelectedPartner(partner);
            setIsEditMode(true);
            setFormData({
                title: partner.title,
                logo: partner.logo,
                link: partner.link,
                isActive: partner.isActive,
                displayOrder: partner.displayOrder,
            });
        } else {
            setIsEditMode(false);
            setSelectedPartner(null);
            const maxOrder = partners.length > 0 ? Math.max(...partners.map(p => p.displayOrder)) : 0;
            setFormData({
                title: '',
                logo: '',
                link: '',
                isActive: true,
                displayOrder: maxOrder + 1,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPartner(null);
        setIsEditMode(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
        }));
    };

    const handleFileUpload = async (file: File) => {
        try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            uploadFormData.append('folder', 'partners');

            const response = await api.post('/upload', uploadFormData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const API_URL = process.env.REACT_APP_API_URL || 'https://api.crm.we4task.com';
            const imageUrl = response.data.fullUrl || (response.data.url?.startsWith('http')
                ? response.data.url
                : `${API_URL}${response.data.url}`);

            setFormData(prev => ({ ...prev, logo: imageUrl }));
            toast.success('Logo uploaded successfully');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to upload logo');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate URL
        if (!formData.link.startsWith('http://') && !formData.link.startsWith('https://')) {
            toast.error('Please enter a valid URL starting with http:// or https://');
            return;
        }

        try {
            if (isEditMode && selectedPartner) {
                await api.put(`/partners/admin/${selectedPartner._id}`, formData);
                toast.success('Partner updated successfully');
            } else {
                await api.post('/partners/admin', formData);
                toast.success('Partner created successfully');
            }
            handleCloseModal();
            fetchPartners();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to save partner');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this partner?')) return;

        try {
            await api.delete(`/partners/admin/${id}`);
            toast.success('Partner deleted successfully');
            fetchPartners();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete partner');
        }
    };

    const handleToggleActive = async (partner: Partner) => {
        try {
            await api.put(`/partners/admin/${partner._id}`, {
                ...partner,
                isActive: !partner.isActive
            });
            toast.success(`Partner ${!partner.isActive ? 'activated' : 'deactivated'}`);
            fetchPartners();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update partner');
        }
    };

    const handleReorder = async (partnerId: string, direction: 'up' | 'down') => {
        const partnerIndex = partners.findIndex(p => p._id === partnerId);
        if (partnerIndex === -1) return;

        const partner = partners[partnerIndex];
        const swapIndex = direction === 'up' ? partnerIndex - 1 : partnerIndex + 1;

        if (swapIndex < 0 || swapIndex >= partners.length) return;

        const swapPartner = partners[swapIndex];

        try {
            await Promise.all([
                api.put(`/partners/admin/${partner._id}`, { ...partner, displayOrder: swapPartner.displayOrder }),
                api.put(`/partners/admin/${swapPartner._id}`, { ...swapPartner, displayOrder: partner.displayOrder })
            ]);
            fetchPartners();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to reorder partners');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="spinner"></div>
                    <p className="mt-4 text-gray-600">Loading partners...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Partners Management</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Add Partner
                </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logo</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {partners.map((partner, index) => (
                            <tr key={partner._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <img
                                        src={partner.logo}
                                        alt={partner.title}
                                        className="h-12 w-auto object-contain"
                                    />
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{partner.title}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <a
                                        href={partner.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 hover:text-blue-800 truncate block max-w-xs"
                                    >
                                        {partner.link}
                                    </a>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                        onClick={() => handleToggleActive(partner)}
                                        className={`px-2 py-1 text-xs font-semibold rounded-full ${partner.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}
                                    >
                                        {partner.isActive ? 'Active' : 'Inactive'}
                                    </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm text-gray-900">{partner.displayOrder}</span>
                                        <div className="flex flex-col">
                                            <button
                                                onClick={() => handleReorder(partner._id, 'up')}
                                                disabled={index === 0}
                                                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                            >
                                                <ArrowUpIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleReorder(partner._id, 'down')}
                                                disabled={index === partners.length - 1}
                                                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                            >
                                                <ArrowDownIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={() => handleOpenModal(partner)}
                                            className="text-indigo-600 hover:text-indigo-900"
                                        >
                                            <PencilIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(partner._id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {partners.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-gray-500">No partners found. Create your first partner!</p>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <form onSubmit={handleSubmit} className="bg-white">
                                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        {isEditMode ? 'Edit Partner' : 'Add New Partner'}
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="px-6 py-4 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            required
                                            maxLength={100}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Link *</label>
                                        <input
                                            type="url"
                                            name="link"
                                            value={formData.link}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="https://example.com"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Logo *</label>
                                        <p className="text-xs text-gray-500 mb-2">📐 Recommended: 200x80px (PNG with transparent background)</p>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                name="logo"
                                                value={formData.logo}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Image URL"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <label className="px-3 py-2 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200">
                                                <PhotoIcon className="w-5 h-5" />
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                                                />
                                            </label>
                                        </div>
                                        {formData.logo && (
                                            <img
                                                src={formData.logo}
                                                alt="Preview"
                                                className="mt-2 h-20 w-auto object-contain border rounded"
                                            />
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                                        <input
                                            type="number"
                                            name="displayOrder"
                                            value={formData.displayOrder}
                                            onChange={handleInputChange}
                                            min={0}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label className="ml-2 block text-sm text-gray-700">Active</label>
                                    </div>
                                </div>

                                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        {isEditMode ? 'Update' : 'Create'} Partner
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PartnersManagement;
